import React, { useEffect, useState, useMemo } from "react";
import { useParseIdQueryMutation } from "../api/idQueryApi";
import { ProductListParams } from "./product-list/ProductListURLConfig";
import { Button, Dropdown, Icon } from "../components/tailwind";
import { copyToClipboard } from "../utils/clipboardUtils";

// Helper function to generate the href to navigate to idQuery page while
// preserving the params from document list
// Has similar logic to setCustomQueryContent in CustomQuery.tsx
export const generateIdQueryHref = ({
  customerSlug,
}: {
  customerSlug: string;
}): string => {
  const params = new URLSearchParams(window.location.search);
  const baseURL = `/d/${customerSlug}/products`;
  params.delete("view");
  params.delete("viewset");
  const queryHref = `${baseURL}/id-query?${params}`;
  return queryHref;
};

type IdType = {
  value: string;
  label: string;
};

const ID_TYPES: IdType[] = [
  { value: "external_id", label: "SKU" },
  { value: "ean", label: "EAN" },
  { value: "product_id", label: "UUID" },
  { value: "inriver_id", label: "Inriver ID" },
];

type Props = {
  preserveParams?: string;
};

export const IdQuery: React.FC<Props> = ({ preserveParams }) => {
  const parsedPreserveParams = useMemo<ProductListParams>(() => {
    if (!preserveParams) return {};
    try {
      return JSON.parse(preserveParams);
    } catch (error) {
      console.error("Failed to parse preserveParams:", error);
      return {};
    }
  }, [preserveParams]);
  const [
    parseCurrentIdQuery,
    {
      isLoading: isQueryResponseLoading,
      data: queryResponseData,
      isSuccess: isQueryResponseSuccess,
    },
  ] = useParseIdQueryMutation();
  const [query, setQuery] = useState<string>("");
  const [selectedIdType, setSelectedIdType] = useState<string>("external_id");
  const [disabledContinueButton, setDisabledContinueButton] = useState(true);
  const [idList, setIdList] = useState<string[]>([]);
  const [notFoundIds, setNotFoundIds] = useState<string[]>([]);
  const [foundIds, setFoundIds] = useState<string[]>([]);
  useEffect(() => {
    setDisabledContinueButton(
      isQueryResponseLoading || !isQueryResponseSuccess
    );
    setIdList(queryResponseData?.id_list ?? []);
    setNotFoundIds(queryResponseData?.not_found_ids ?? []);
    setFoundIds(queryResponseData?.found_ids ?? []);
  }, [isQueryResponseLoading, isQueryResponseSuccess, queryResponseData]);

  const handleClickContinue = (): void => {
    if (queryResponseData?.redirect_url) {
      window.location.href = queryResponseData.redirect_url;
    }
  };

  return (
    <div className="tw-w-11/12 tw-mx-auto tw-py-8" data-testid="id-query">
      <h1
        className="tw-text-2xl tw-font-bold tw-mb-4"
        data-testid="id-query-title"
      >
        Search with a list of IDs
      </h1>
      <span className="tw-text-gray-500 tw-mb-4">
        Search with a list of IDs separated by commas or newlines (e.g. 123,
        456, 789)
        <br />
        Select the identifier type and enter the IDs you want to search for.
      </span>
      <div className="tw-flex tw-flex-col tw-gap-2 tw-mt-4">
        <div className="tw-flex tw-gap-4 tw-items-center">
          <div className="tw-flex tw-flex-col tw-gap-1">
            <label className="tw-text-sm tw-font-medium tw-text-gray-700">
              Identifier
            </label>
            <Dropdown<string>
              control="search"
              options={ID_TYPES.map((type) => ({
                key: type.value,
                value: type.value,
                text: type.label,
              }))}
              value={selectedIdType}
              onChange={(value): void => {
                setSelectedIdType(value as string);
              }}
              placeholder="Select identifier type"
              testIdBase="id-type"
            />
          </div>
        </div>
        <textarea
          data-testid="id-query-textarea"
          value={query}
          placeholder="123, 456, 789"
          rows={5}
          onChange={(e): void => {
            const newQuery = e.currentTarget.value;
            setQuery(newQuery);
            setDisabledContinueButton(true);
            setIdList([]);
            setNotFoundIds([]);
            setFoundIds([]);
          }}
          className="tw-border tw-rounded-md tw-p-2"
        />
        {isQueryResponseSuccess && !queryResponseData?.error_message && (
          <div className="tw-grid tw-grid-cols-[repeat(3,_auto)] tw-divide-x tw-rounded-md tw-border tw-text-sm">
            <div className="tw-px-4 tw-py-2">
              <div className="tw-font-medium" data-testid="total-ids">
                {idList?.length}
              </div>
              <div className="tw-text-gray-500">Total IDs</div>
            </div>
            <div className="tw-px-4 tw-py-2">
              <div
                className="tw-font-medium tw-text-green-600"
                data-testid="found-ids-count"
              >
                {foundIds?.length}
              </div>
              <div className="tw-text-gray-500">Found</div>
            </div>
            <div className="tw-px-4 tw-py-2">
              <div
                className="tw-font-medium tw-text-red-600"
                data-testid="not-found-ids-count"
              >
                {notFoundIds?.length}
              </div>
              <div className="tw-text-gray-500">Not Found</div>
            </div>
          </div>
        )}
        <div className="tw-flex tw-gap-2">
          <Button
            variant="primary-alt"
            content="Search"
            data-testid="button-search"
            disabled={isQueryResponseLoading}
            onClick={(): void => {
              parseCurrentIdQuery({
                id_type: selectedIdType,
                query: query,
                url_params: parsedPreserveParams,
              });
            }}
          />
          <Button
            variant="primary"
            content="Continue"
            data-testid="button-continue"
            disabled={disabledContinueButton || isQueryResponseLoading}
            onClick={handleClickContinue}
          />
        </div>
      </div>
      <div className="tw-grid tw-grid-cols-2 tw-gap-8 tw-mt-8">
        <div className="tw-border tw-rounded-lg tw-p-4">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
            <h2 className="tw-text-lg tw-font-bold tw-flex tw-items-center tw-gap-2">
              Found IDs
              <Icon className="tw-text-green-500" name="zoom_in" />
            </h2>
            <Button
              className="txu-btn txu-secondary tw-text-sm"
              content="Copy"
              onClick={(): void => {
                copyToClipboard(foundIds.join("\n"));
              }}
              data-testid="button-copy-found-ids"
            />
          </div>
          <ul
            className="tw-list-none tw-space-y-1"
            data-testid="found-ids-list"
          >
            {foundIds?.map((id) => (
              <li key={id}>{id}</li>
            ))}
          </ul>
        </div>
        <div className="tw-border tw-rounded-lg tw-p-4">
          <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
            <h2 className="tw-text-lg tw-font-bold tw-flex tw-items-center tw-gap-2">
              Not Found IDs
              <Icon className="tw-text-red-500" name="zoom_out" />
            </h2>
            <Button
              className="txu-btn txu-secondary tw-text-sm"
              content="Copy"
              onClick={(): void => {
                copyToClipboard(notFoundIds.join("\n"));
              }}
              data-testid="button-copy-not-found-ids"
            />
          </div>
          <ul
            className="tw-list-none tw-space-y-1"
            data-testid="not-found-ids-list"
          >
            {notFoundIds?.map((id) => (
              <li key={id}>{id}</li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};
